import { useEffect, useMemo, useState } from 'react';

import { useNavigate } from 'react-router-dom';
import { CircularProgress } from '@mui/material';

import RichText from 'components/RichText/RichText';
import Checkbox from 'components/Checkbox/Checkbox';
import {
  ESwitchOptions,
  SWITCH_OPTIONS,
  getCountriesOptions,
  getVersionNextStatus,
  RELEASE_STATUS_TO_COLOR_MAP
} from 'views/TestAndRelease/utils';
import Input from 'components/Input/Input';
import { ReleaseStatus } from 'common/contracts';
import { useColumns } from '../../hooks/useColumns';
import { showToast, EToasterStatus } from 'utils/showToast';
import { capitalizeFirstLetter } from 'utils/formattingUtil';
import DataTable from 'components/DataTableComponent/DataTable';
import PortalHeader from 'components/PortalHeader/PortalHeader';
import useDistributionManager from 'api/useDistributionManager';
import SwitchSelector from 'components/SwitchSelector/SwitchSelector';
import InputSelectDropdown from 'components/InputSelectDropdown/InputSelectDropdown';

const countriesOptions = getCountriesOptions();

export function Release() {
  const navigate = useNavigate();
  const { getVersions, createVersion } = useDistributionManager();
  const versions = getVersions.data;
  const versionOptions = useMemo(
    () =>
      versions?.map((version, index) => ({
        label: `${version.versionName}${index === 0 ? ' (latest)' : ''}`,
        value: version.versionCode
      })) || [],
    [versions]
  );
  const [selectedVersion, setSelectedVersion] = useState(versionOptions[0]);
  const [isHardRelease, setIsHardRelease] = useState(false);
  const [searchCountry, setSearchCountry] = useState('');
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const columns = useColumns();

  const selectedVersionObj = versions?.find(
    (version) => version.versionCode === selectedVersion?.value
  );

  const filteredCountries = useMemo(() => {
    return countriesOptions.filter((country: any) =>
      country.label.toLowerCase().includes(searchCountry.toLowerCase())
    );
  }, [searchCountry]);

  const selectedCountriesState = useMemo(() => {
    return selectedCountries.reduce(
      (acc, country) => ({
        ...acc,
        [country]: true
      }),
      {} as Record<string, boolean>
    );
  }, [selectedCountries]);

  useEffect(() => {
    setSelectedVersion(versionOptions[0]);
  }, [versionOptions]);

  useEffect(() => {
    if (selectedVersionObj) {
      setSelectedCountries(selectedVersionObj?.testRules?.countryCodes || []);
    }
  }, [selectedVersionObj]);

  const handleEditRelease = () => {
    const isAllCountriesSelected =
      selectedCountries.length === countriesOptions.length;
    const nextVersionStatus = getVersionNextStatus(
      isAllCountriesSelected,
      isHardRelease
    );
    if (selectedVersionObj) {
      const newCountries = Array.from(new Set(selectedCountries));
      createVersion.mutate(
        {
          ...selectedVersionObj,
          status: nextVersionStatus,
          testRules: {
            customerIds: [
              ...(selectedVersionObj?.testRules?.customerIds || [])
            ],
            countryCodes: newCountries
          }
        },
        {
          onSuccess: () => {
            showToast({
              message: 'The release has been updated successfully',
              status: EToasterStatus.SUCCESS
            });
          },
          onError: () => {
            showToast({
              message: 'Failed to update the release',
              status: EToasterStatus.ERROR
            });
          }
        }
      );
    }
  };

  const isAllCountriesSelected =
    selectedCountries.length === countriesOptions.length;
  const isNoCountriesSelected =
    Object.values(selectedCountriesState).length === 0;

  const status = selectedVersionObj?.versionStatuses?.releaseStatus;
  const isReleaseBlocked = status === ReleaseStatus.BLOCKED;
  const isReleaseReady = status === ReleaseStatus.READY;
  const isReleaseInProgress = status === ReleaseStatus.IN_PROGRESS;
  const isNewVersionAvailable = status === ReleaseStatus.NEW_VERSION_AVAILABLE;

  if (getVersions.isLoading) {
    return <CircularProgress className="absolute top-1/2 left-1/2 h-16 w-16" />;
  }

  return (
    <div className="w-full mx-auto">
      <PortalHeader
        title="Test & Release"
        primaryButtonText={
          isReleaseBlocked || isReleaseReady ? 'Save & Release' : 'Save'
        }
        primaryButtonState={{
          disabled:
            isReleaseBlocked ||
            !selectedVersionObj ||
            isNewVersionAvailable ||
            createVersion.isLoading ||
            isNoCountriesSelected,
          isLoading: createVersion.isLoading
        }}
        onPrimaryButtonClick={handleEditRelease}
      />
      <div className="w-full px-9 gap-3">
        <div className="w-full max-w-[220px]">
          <SwitchSelector
            options={SWITCH_OPTIONS}
            onClick={(value) => navigate(`../${value}`)}
            value={ESwitchOptions.RELEASE}
          />
        </div>
      </div>
      <div className="flex p-9 gap-8">
        <div className="w-[250px]">
          <RichText
            title="Current Version"
            description="Select the application version you want to release."
          />
        </div>
        <div className="flex-1">
          <div className="border border-base-sidebarBorder rounded-[20px] p-6">
            <div className="relative max-w-[320px]">
              <InputSelectDropdown
                label="Version"
                options={versionOptions}
                defaultSelected={versionOptions[0]}
                onOptionChange={(value: string) => {
                  const selectedVersion = versionOptions.find(
                    (option) => option.value === value
                  );
                  if (selectedVersion) {
                    setSelectedVersion(selectedVersion);
                  }
                }}
              />
              <div className="mt-2 flex justify-between">
                {status && (
                  <span
                    className={`${RELEASE_STATUS_TO_COLOR_MAP[status]} text-sm`}
                  >
                    {capitalizeFirstLetter(status.toLowerCase())}
                  </span>
                )}
                <Checkbox
                  label="Hard release"
                  isDisabled={isNewVersionAvailable || !isAllCountriesSelected}
                  checked={isHardRelease}
                  onCheckedChange={(checked) => setIsHardRelease(!!checked)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex pl-9 pr-9 gap-8">
        <div className="w-[250px]">
          <RichText title="Targeted countries" />
        </div>
        <div className="flex-1">
          <div className="border border-base-sidebarBorder rounded-[20px] p-6">
            <div className="mb-4">
              <div className="flex gap-2 max-w-[410px] items-end">
                <Input
                  label="Country"
                  placeholder="Search country"
                  type="text"
                  value={searchCountry}
                  onChange={(e) => setSearchCountry(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <DataTable
                columns={columns}
                data={filteredCountries}
                getRowId={(row) => row.value as string}
                rowSelectionConfig={{
                  enableRowSelection: true,
                  isCheckboxesDisabled:
                    isNewVersionAvailable || createVersion.isLoading,
                  selectedRowsState: selectedCountriesState,
                  onRowSelectionChange: (selectedRows) => {
                    setSelectedCountries(
                      selectedRows.map((row) => String(row.value))
                    );
                  }
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
